import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Badge, Btn, Card, HeaderBack, Input, RNLLoader, Select } from '../UI';
import * as api from '../../api';
import { BOQ_BASE } from './paths';

// BOQ workbook importer (BOQ Hub → a project → Import BOQ).
// 1. upload: the server reads the .xlsx and prices every line again with the workbook's own Settings rates;
//    nothing is imported. 2. preview: buildings, disciplines, totals against the Summary sheet, problems.
// 3. confirm: everything goes in at once as a new revision of a package; if anything fails nothing is saved.

const naira = (v) => (v == null ? '—'
  : `${Number(v) < 0 ? '−' : ''}₦${Math.abs(Number(v)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const pct = (v) => (v == null ? '—' : `${+(Number(v) * 100).toFixed(2)}%`);
const today = () => new Date().toISOString().slice(0, 10);
const NEW = '__new__';

const RATE_LABELS = [
  ['labour_gang_rate', 'Labour gang rate', (v) => `${naira(v)}/hr`], ['landed_uplift', 'Landed uplift', pct],
  ['accessories', 'Accessories', pct], ['transport', 'Transport', pct], ['equipment', 'Equipment & tools', pct],
  ['prelims', 'Prelims', pct], ['material_margin', 'Material margin', pct], ['labour_margin', 'Labour margin', pct],
  ['contingency', 'Contingency', pct], ['vat', 'VAT', pct],
];

const box = { border: '1px solid var(--border-light)', borderRadius: 12, padding: 16, background: 'var(--bg-card, #fff)' };
const label = { fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', color: 'var(--text-secondary)', textTransform: 'uppercase' };

function Stat({ title, value, tone }) {
  return (
    <div style={{ ...box, padding: '12px 14px', minWidth: 0 }}>
      <div style={label}>{title}</div>
      <div style={{ fontSize: 15, fontWeight: 800, marginTop: 4, color: tone ? `var(--${tone})` : 'var(--text-primary)', fontFamily: 'var(--mono)', overflowWrap: 'anywhere' }}>{value}</div>
    </div>
  );
}

function IssueList({ title, items, total, tone }) {
  const [open, setOpen] = useState(tone === 'red');
  if (!total) return null;
  return (
    <div style={{ ...box, borderColor: `var(--${tone})` }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ all: 'unset', cursor: 'pointer', display: 'flex', gap: 8, alignItems: 'center', fontWeight: 800, color: `var(--${tone})` }}>
        {open ? '▾' : '▸'} {title} ({total})
      </button>
      {open && (
        <ul style={{ margin: '10px 0 0', paddingLeft: 18, fontSize: 13, maxHeight: 280, overflowY: 'auto' }}>
          {items.map((i, n) => (
            <li key={n} style={{ marginBottom: 4 }}>
              {i.sheet && <b>{i.sheet}{i.row ? ` row ${i.row}` : ''}: </b>}{i.message}
            </li>
          ))}
          {total > items.length && <li><i>…and {total - items.length} more</i></li>}
        </ul>
      )}
    </div>
  );
}

function History({ imports, projectId }) {
  const navigate = useNavigate();
  if (!imports.length) return null;
  return (
    <div style={{ marginTop: 28 }}>
      <div style={{ ...label, marginBottom: 10 }}>Earlier uploads</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ textAlign: 'left' }}>
            {['File', 'Uploaded', 'Items', 'Amount', 'Status', ''].map(h => <th key={h} style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-light)' }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {imports.map(i => (
              <tr key={i.id}>
                <td style={{ padding: '6px 8px' }}>{i.original_name}</td>
                <td style={{ padding: '6px 8px' }}>{(i.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                <td style={{ padding: '6px 8px' }}>{i.totals?.items ?? '—'}</td>
                <td style={{ padding: '6px 8px', fontFamily: 'var(--mono)' }}>{naira(i.totals?.amount)}</td>
                <td style={{ padding: '6px 8px' }}><Badge color={i.status === 'imported' ? 'green' : 'gray'}>{i.status === 'imported' ? 'IMPORTED' : 'PREVIEW ONLY'}</Badge></td>
                <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                  {i.status === 'imported' && i.boq_package_id && i.boq_revision_id && (
                    <Btn size="sm" variant="ghost" onClick={() => navigate(`${BOQ_BASE}/project/${projectId}/packages/${i.boq_package_id}/revisions/${i.boq_revision_id}/boqs`)}>Open revision</Btn>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function BOQImportView({ toast, projects = [] }) {
  const { projectId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const project = projects.find(p => (p.project_id || p.id) === projectId);

  const [loading, setLoading] = useState(true);
  const [packages, setPackages] = useState([]);
  const [packageTypes, setPackageTypes] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [imports, setImports] = useState([]);

  const [file, setFile] = useState(null);
  const [reading, setReading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [openBuilding, setOpenBuilding] = useState(null);

  const [pkgChoice, setPkgChoice] = useState(params.get('package') || NEW);
  const [newPkg, setNewPkg] = useState({ code: '', name: '', description: '', package_type_id: '' });
  const [rev, setRev] = useState({ code: '', name: '', revision_date: today(), description: '' });
  const [divMap, setDivMap] = useState({});
  const [importing, setImporting] = useState(false);
  const [done, setDone] = useState(null);

  const loadLists = async () => {
    const [pk, types, divs, imps] = await Promise.all([
      api.listProjectPackages(projectId), api.getPackageTypes(), api.listProjectDivisions(projectId).catch(() => []),
      api.listBoqImports(projectId).catch(() => []),
    ]);
    setPackages(pk || []);
    setPackageTypes(types || []);
    setDivisions(Array.isArray(divs) ? divs : (divs?.items || divs?.data || []));
    setImports(imps || []);
  };

  useEffect(() => {
    setLoading(true);
    loadLists().catch(() => toast?.show?.('Could not load the project’s packages', 'error')).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const s = preview?.summary;

  const read = async () => {
    if (!file) return;
    setReading(true);
    setDone(null);
    try {
      const res = await api.previewBoqImport(projectId, file);
      setPreview(res);
      setDivMap({});
      const base = (res.original_name || '').replace(/\.xlsx$/i, '');
      setNewPkg(p => ({ ...p, name: p.name || base }));
      setRev(r => ({ ...r, name: r.name || `Imported ${base}` }));
      setImports(await api.listBoqImports(projectId).catch(() => imports));
    } catch (e) {
      toast?.show?.(e.message || 'The workbook could not be read', 'error');
    }
    setReading(false);
  };

  const divisionOptions = useMemo(() => divisions.map(d => ({ value: d.id || d.project_division_id, label: d.code ? `${d.name} (${d.code})` : d.name })), [divisions]);

  const ready = s?.can_import && rev.code.trim() && rev.name.trim() && rev.revision_date
    && (pkgChoice !== NEW || (newPkg.code.trim() && newPkg.name.trim() && newPkg.package_type_id));

  const confirm = async () => {
    setImporting(true);
    try {
      const division_map = {};
      Object.entries(divMap).forEach(([sheet, v]) => { if (v && v !== NEW) division_map[sheet] = v; });
      const payload = {
        revision: { ...rev, code: rev.code.trim(), name: rev.name.trim(), description: rev.description || null },
        division_map,
        ...(pkgChoice === NEW
          ? { new_package: { ...newPkg, code: newPkg.code.trim(), name: newPkg.name.trim(), description: newPkg.description || null } }
          : { package_id: pkgChoice }),
      };
      const res = await api.confirmBoqImport(preview.id, payload);
      setDone(res.result);
      setPreview(null);
      setFile(null);
      toast?.show?.(`Imported ${res.result?.item_count} items`, 'success');
      loadLists().catch(() => {});
    } catch (e) {
      toast?.show?.(e.message || 'The import failed — nothing was saved', 'error');
    }
    setImporting(false);
  };

  if (loading) return <RNLLoader title="Loading…" />;

  return (
    <div className="fade-in">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <HeaderBack onClick={() => navigate(`${BOQ_BASE}/project/${projectId}/packages`)}>Back to Packages</HeaderBack>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>Import BOQ workbook{project ? ` — ${project.project_name}` : ''}</h2>
      </div>

      {done && (
        <Card style={{ padding: 20, marginBottom: 20, borderColor: 'var(--green)' }}>
          <div style={{ fontWeight: 800, fontSize: 16, color: 'var(--green)' }}>Imported</div>
          <div style={{ marginTop: 6, fontSize: 14 }}>
            {done.item_count} items in {done.boq_count} BOQs went into {done.package?.code} / {done.revision?.code} ({done.revision?.name}).
            {done.divisions_created?.length > 0 && <> New buildings/zones: {done.divisions_created.join(', ')}.</>}
          </div>
          <div style={{ marginTop: 12 }}>
            <Btn onClick={() => navigate(`${BOQ_BASE}/project/${projectId}/packages/${done.package.id}/revisions/${done.revision.id}/boqs`)}>Open the revision</Btn>
          </div>
        </Card>
      )}

      {/* Step 1 — upload */}
      <Card style={{ padding: 20 }}>
        <div style={{ ...label, marginBottom: 8 }}>1 · Workbook</div>
        <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--text-secondary)' }}>
          The MEP bill template: a Settings sheet with the costing rates, a Summary sheet and one sheet per building.
          Excel (.xlsx), up to 10 MB. Reading it changes nothing — you will see what it contains first.
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={e => { setFile(e.target.files?.[0] || null); setPreview(null); }} />
          <Btn onClick={read} loading={reading} disabled={!file}>Read workbook</Btn>
        </div>
      </Card>

      {s && (
        <>
          {/* Step 2 — preview */}
          <Card style={{ padding: 20, marginTop: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={label}>2 · What the workbook contains — {preview.original_name}</div>
              {s.can_import ? <Badge color="green">READY TO IMPORT</Badge> : <Badge color="red">FIX THE PROBLEMS FIRST</Badge>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 10, marginTop: 14 }}>
              <Stat title="BOQ items" value={s.totals.items.toLocaleString()} />
              <Stat title="Buildings" value={s.buildings.length} />
              <Stat title="Sub-total (contractor qty)" value={naira(s.totals.amount)} />
              <Stat title="At consultant qty" value={naira(s.totals.consultant_amount)} />
              <Stat title="Variance" value={naira(s.totals.variance)} tone={s.totals.variance > 0 ? 'amber' : undefined} />
              <Stat title={`Contingency ${pct(s.rates.contingency)}`} value={naira(s.totals.contingency)} />
              <Stat title={`VAT ${pct(s.rates.vat)}`} value={naira(s.totals.vat)} />
              <Stat title="Grand total incl. VAT" value={naira(s.totals.grand_total)} />
            </div>

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 14 }}>
              {RATE_LABELS.map(([k, t, fmt]) => (
                <span key={k} style={{ fontSize: 12, padding: '4px 8px', border: '1px solid var(--border-light)', borderRadius: 999 }}>
                  {t}: <b>{fmt(s.rates[k])}</b>
                </span>
              ))}
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '10px 0 0' }}>
              Every line was priced again here with these rates; that is what will be saved. Contingency and VAT stay on the summary — the BOQs carry the sub-total.
            </p>

            <div style={{ display: 'grid', gap: 10, marginTop: 14 }}>
              <IssueList title="Problems that stop the import" items={s.issues.errors} total={s.issues.error_count} tone="red" />
              <IssueList title="Things to check" items={s.issues.warnings} total={s.issues.warning_count} tone="amber" />
            </div>

            <div style={{ overflowX: 'auto', marginTop: 16 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ textAlign: 'left' }}>
                  {['Building (sheet)', 'Items', 'Disciplines', 'Amount', 'Summary sheet', 'Load into building / zone'].map(h => (
                    <th key={h} style={{ padding: '8px', borderBottom: '2px solid var(--border-light)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {s.buildings.map(b => {
                    const diff = b.summary_amount == null ? null : Math.abs(b.summary_amount - b.amount);
                    const choice = divMap[b.sheet] ?? (b.division?.id || NEW);
                    return (
                      <React.Fragment key={b.sheet}>
                        <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
                          <td style={{ padding: 8 }}>
                            <button type="button" onClick={() => setOpenBuilding(o => (o === b.sheet ? null : b.sheet))}
                              style={{ all: 'unset', cursor: 'pointer', fontWeight: 700 }}>
                              {openBuilding === b.sheet ? '▾' : '▸'} {b.name} <span style={{ fontFamily: 'var(--mono)', color: 'var(--text-secondary)' }}>{b.code}</span>
                            </button>
                          </td>
                          <td style={{ padding: 8 }}>{b.item_count}</td>
                          <td style={{ padding: 8 }}>{b.sections.length}</td>
                          <td style={{ padding: 8, fontFamily: 'var(--mono)', whiteSpace: 'nowrap' }}>{naira(b.amount)}</td>
                          <td style={{ padding: 8 }}>
                            {diff == null ? <span style={{ color: 'var(--text-secondary)' }}>—</span>
                              : diff <= 1 ? <Badge color="green">MATCHES</Badge>
                                : <Badge color="amber">{naira(b.summary_amount)}</Badge>}
                          </td>
                          <td style={{ padding: 8, minWidth: 220 }}>
                            <Select value={choice} onChange={v => setDivMap(m => ({ ...m, [b.sheet]: v }))}
                              options={[{ value: NEW, label: b.division ? `Create new “${b.name}”` : `Create new “${b.name}” (no match)` }, ...divisionOptions]} />
                          </td>
                        </tr>
                        {openBuilding === b.sheet && b.sections.map(sec => (
                          <tr key={sec.code} style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                            <td style={{ padding: '4px 8px 4px 28px' }}>{sec.name}</td>
                            <td style={{ padding: '4px 8px' }}>{sec.item_count}</td>
                            <td />
                            <td style={{ padding: '4px 8px', fontFamily: 'var(--mono)' }}>{naira(sec.amount)}</td>
                            <td colSpan={2} style={{ padding: '4px 8px' }}>cost {naira(sec.cost)} · at consultant qty {naira(sec.consultant_amount)}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Step 3 — where it goes */}
          {s.can_import && (
            <Card style={{ padding: 20, marginTop: 20 }}>
              <div style={{ ...label, marginBottom: 12 }}>3 · Import as a new revision</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
                <Select label="Package" value={pkgChoice} onChange={setPkgChoice}
                  options={[{ value: NEW, label: '+ New package' }, ...packages.map(p => ({ value: p.id, label: `${p.code} — ${p.name}` }))]} />
                {pkgChoice === NEW && <>
                  <Input label="Package code" required value={newPkg.code} maxLength={50} onChange={v => setNewPkg(p => ({ ...p, code: v }))} placeholder="e.g. PKG-MEP-01" />
                  <Input label="Package name" required value={newPkg.name} maxLength={200} onChange={v => setNewPkg(p => ({ ...p, name: v }))} />
                  <Select label="Package type" required value={newPkg.package_type_id} onChange={v => setNewPkg(p => ({ ...p, package_type_id: v }))}
                    options={[{ value: '', label: '— Select —' }, ...packageTypes.map(t => ({ value: t.id, label: t.name }))]} />
                </>}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14, marginTop: 14 }}>
                <Input label="Revision code" required value={rev.code} maxLength={50} onChange={v => setRev(r => ({ ...r, code: v }))} placeholder="e.g. REV-00" />
                <Input label="Revision name" required value={rev.name} maxLength={200} onChange={v => setRev(r => ({ ...r, name: v }))} />
                <Input label="Revision date" type="date" required value={rev.revision_date} onChange={v => setRev(r => ({ ...r, revision_date: v }))} />
                <Input label="Description" value={rev.description} onChange={v => setRev(r => ({ ...r, description: v }))} />
              </div>
              {s.buildings.some(b => (divMap[b.sheet] ?? (b.division?.id || NEW)) === NEW) && (
                <p style={{ fontSize: 13, margin: '14px 0 0' }}>
                  New buildings/zones will be created for: <b>{s.buildings.filter(b => (divMap[b.sheet] ?? (b.division?.id || NEW)) === NEW).map(b => b.name).join(', ')}</b>.
                </p>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
                <Btn variant="ghost" onClick={() => setPreview(null)} disabled={importing}>Cancel</Btn>
                <Btn onClick={confirm} loading={importing} disabled={!ready}>Import {s.totals.items.toLocaleString()} items</Btn>
              </div>
              {importing && <p style={{ fontSize: 12, color: 'var(--text-secondary)', textAlign: 'right', margin: '8px 0 0' }}>Saving everything in one go — this can take a little while for a large bill.</p>}
            </Card>
          )}
        </>
      )}

      <History imports={imports} projectId={projectId} />
    </div>
  );
}
